import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

export default function Datenschutz() {
  const { t } = useTranslation();
  const isEN = i18n.language === 'en';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 mb-6 inline-block">
        ← {t('footer.backToHome')}
      </Link>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('footer.datenschutz')}</h1>
      <p className="text-sm text-gray-400 mb-8">{isEN ? 'Version: April 2026' : 'Stand: April 2026'}</p>

      <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">

        {isEN ? (
          <>
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Controller</h2>
              <p>
                The controller within the meaning of the GDPR is:<br /><br />
                Timm Buse<br />
                Meiereistraße 6<br />
                24960 Munkbrarup<br />
                Email: <a href="mailto:grenzland-liga@padelmap.app" className="text-primary-600 hover:text-primary-700">grenzland-liga@padelmap.app</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Data collected and purposes</h2>
              <p className="mb-3">
                We collect and process the following personal data solely for the purpose of operating the league:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-3 border border-gray-200 font-semibold">Data</th>
                      <th className="text-left p-3 border border-gray-200 font-semibold">Purpose</th>
                      <th className="text-left p-3 border border-gray-200 font-semibold">Legal basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-3 border border-gray-200">First and last name</td>
                      <td className="p-3 border border-gray-200">Identification, public player list</td>
                      <td className="p-3 border border-gray-200">Art. 6(1)(b) GDPR (contract)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-3 border border-gray-200">Email address</td>
                      <td className="p-3 border border-gray-200">Account management, notifications</td>
                      <td className="p-3 border border-gray-200">Art. 6(1)(b) GDPR (contract)</td>
                    </tr>
                    <tr>
                      <td className="p-3 border border-gray-200">Password (encrypted)</td>
                      <td className="p-3 border border-gray-200">Authentication</td>
                      <td className="p-3 border border-gray-200">Art. 6(1)(b) GDPR (contract)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-3 border border-gray-200">Phone number (optional)</td>
                      <td className="p-3 border border-gray-200">Scheduling with opponents (only when enabled)</td>
                      <td className="p-3 border border-gray-200">Art. 6(1)(a) GDPR (consent)</td>
                    </tr>
                    <tr>
                      <td className="p-3 border border-gray-200">Profile photo (optional)</td>
                      <td className="p-3 border border-gray-200">Public profile page</td>
                      <td className="p-3 border border-gray-200">Art. 6(1)(a) GDPR (consent)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-3 border border-gray-200">Skill level, about-me text (optional)</td>
                      <td className="p-3 border border-gray-200">Public profile, partner search</td>
                      <td className="p-3 border border-gray-200">Art. 6(1)(a) GDPR (consent)</td>
                    </tr>
                    <tr>
                      <td className="p-3 border border-gray-200">Match results</td>
                      <td className="p-3 border border-gray-200">League tables, statistics</td>
                      <td className="p-3 border border-gray-200">Art. 6(1)(b) GDPR (contract)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-3 border border-gray-200">Match chat messages</td>
                      <td className="p-3 border border-gray-200">Scheduling between players</td>
                      <td className="p-3 border border-gray-200">Art. 6(1)(b) GDPR (contract)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Publicly visible data</h2>
              <p>
                The following data is publicly visible to all visitors of the platform:
                name, skill level, profile photo, about-me text, and match results.
                Phone numbers are only shown to the respective opponent and administrators — and only until a match result has been submitted.
                Displaying your phone number requires your active consent in the profile settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Retention period</h2>
              <p>
                Your data is stored for as long as your account is active. After deletion of your account, personal data will be
                deleted within 30 days, unless statutory retention obligations apply.
                Match results may continue to be stored in anonymised form (without names) for statistical purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Email dispatch (Mailjet)</h2>
              <p>
                For the dispatch of transactional emails (registration confirmation, password reset, league notifications), we use
                the service <strong>Mailjet</strong> (Sinch Sweden AB, Lindhagensgatan 74, 112 18 Stockholm, Sweden — EU-based).
                Mailjet acts as a data processor pursuant to Art. 28 GDPR. A data processing agreement (DPA) exists between us and Mailjet.
                Your email address is transmitted to Mailjet solely for the purpose of sending the above-mentioned emails and is not used for advertising purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Cookies and local storage</h2>
              <p>
                This website does not use tracking or advertising cookies. We only use technically necessary mechanisms:
              </p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li><strong>JWT token (localStorage)</strong> — to maintain your login session</li>
                <li><strong>Language setting (localStorage)</strong> — to save your chosen language</li>
                <li><strong>Cookie consent (localStorage)</strong> — to remember your confirmation of this banner</li>
              </ul>
              <p className="mt-2">
                This data does not leave your browser and is not transmitted to third parties.
                Consent under § 25 TDDDG is not required for technically necessary storage.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Hosting</h2>
              <p>
                The platform is operated on a server located in Germany. When accessing the website, technical connection data
                (IP address, browser type, time) is temporarily stored in server logs. This data is automatically deleted after
                a maximum of 7 days and is not merged with other data.
                Legal basis: Art. 6(1)(f) GDPR (legitimate interest in security and operation).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Your rights</h2>
              <p className="mb-2">Under the GDPR, you have the following rights:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Access</strong> (Art. 15) — what data we have stored about you</li>
                <li><strong>Rectification</strong> (Art. 16) — correction of inaccurate data (directly available in profile settings)</li>
                <li><strong>Erasure</strong> (Art. 17) — deletion of your account and all personal data</li>
                <li><strong>Restriction</strong> (Art. 18) — restriction of processing</li>
                <li><strong>Data portability</strong> (Art. 20) — receipt of your data in machine-readable format</li>
                <li><strong>Objection</strong> (Art. 21) — objection to processing based on legitimate interests</li>
                <li><strong>Withdrawal</strong> — withdrawal of consent at any time with effect for the future</li>
              </ul>
              <p className="mt-3">
                To exercise your rights, please contact us at:{' '}
                <a href="mailto:grenzland-liga@padelmap.app" className="text-primary-600 hover:text-primary-700">grenzland-liga@padelmap.app</a>
              </p>
              <p className="mt-2">
                You also have the right to lodge a complaint with a data protection supervisory authority.
                The competent authority is the <strong>Independent Centre for Privacy Protection Schleswig-Holstein (ULD)</strong>,{' '}
                <a href="https://www.datenschutzzentrum.de" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">
                  www.datenschutzzentrum.de
                </a>.
              </p>
            </section>
          </>
        ) : (
          <>
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Verantwortlicher</h2>
              <p>
                Verantwortlicher im Sinne der DSGVO ist:<br /><br />
                Timm Buse<br />
                Meiereistraße 6<br />
                24960 Munkbrarup<br />
                E-Mail: <a href="mailto:grenzland-liga@padelmap.app" className="text-primary-600 hover:text-primary-700">grenzland-liga@padelmap.app</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Erhobene Daten und Zweck</h2>
              <p className="mb-3">
                Wir erheben und verarbeiten folgende personenbezogene Daten ausschließlich zur Durchführung des Liga-Betriebs:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-3 border border-gray-200 font-semibold">Daten</th>
                      <th className="text-left p-3 border border-gray-200 font-semibold">Zweck</th>
                      <th className="text-left p-3 border border-gray-200 font-semibold">Rechtsgrundlage</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-3 border border-gray-200">Vor- und Nachname</td>
                      <td className="p-3 border border-gray-200">Identifikation, öffentliche Spielerliste</td>
                      <td className="p-3 border border-gray-200">Art. 6 Abs. 1 lit. b DSGVO (Vertrag)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-3 border border-gray-200">E-Mail-Adresse</td>
                      <td className="p-3 border border-gray-200">Konto-Verwaltung, Benachrichtigungen</td>
                      <td className="p-3 border border-gray-200">Art. 6 Abs. 1 lit. b DSGVO (Vertrag)</td>
                    </tr>
                    <tr>
                      <td className="p-3 border border-gray-200">Passwort (verschlüsselt)</td>
                      <td className="p-3 border border-gray-200">Authentifizierung</td>
                      <td className="p-3 border border-gray-200">Art. 6 Abs. 1 lit. b DSGVO (Vertrag)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-3 border border-gray-200">Telefonnummer (optional)</td>
                      <td className="p-3 border border-gray-200">Terminfindung mit Gegnern (nur bei aktivierter Option)</td>
                      <td className="p-3 border border-gray-200">Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</td>
                    </tr>
                    <tr>
                      <td className="p-3 border border-gray-200">Profilfoto (optional)</td>
                      <td className="p-3 border border-gray-200">Öffentliche Profilseite</td>
                      <td className="p-3 border border-gray-200">Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-3 border border-gray-200">Spielniveau, Über-mich-Text (optional)</td>
                      <td className="p-3 border border-gray-200">Öffentliche Profilseite, Partnersuche</td>
                      <td className="p-3 border border-gray-200">Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</td>
                    </tr>
                    <tr>
                      <td className="p-3 border border-gray-200">Spielergebnisse</td>
                      <td className="p-3 border border-gray-200">Liga-Tabellen, Statistiken</td>
                      <td className="p-3 border border-gray-200">Art. 6 Abs. 1 lit. b DSGVO (Vertrag)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-3 border border-gray-200">Match-Chat-Nachrichten</td>
                      <td className="p-3 border border-gray-200">Terminfindung zwischen Spielern</td>
                      <td className="p-3 border border-gray-200">Art. 6 Abs. 1 lit. b DSGVO (Vertrag)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Öffentlich sichtbare Daten</h2>
              <p>
                Folgende Daten sind für alle Besucher der Plattform öffentlich einsehbar:
                Name, Spielniveau, Profilfoto, „Über mich"-Text sowie Spielergebnisse.
                Telefonnummern werden nur dem jeweiligen Gegner und Administratoren angezeigt — und nur so lange, bis ein Spielergebnis eingetragen wurde.
                Die Anzeige der Telefonnummer erfordert deine aktive Einwilligung in den Profileinstellungen.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Speicherdauer</h2>
              <p>
                Deine Daten werden gespeichert, solange dein Konto aktiv ist. Nach Löschung deines Kontos werden
                personenbezogene Daten innerhalb von 30 Tagen gelöscht, soweit keine gesetzlichen Aufbewahrungspflichten bestehen.
                Spielergebnisse können in anonymisierter Form (ohne Namensnennung) für statistische Zwecke weiterhin gespeichert werden.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. E-Mail-Versand (Mailjet)</h2>
              <p>
                Für den Versand transaktionaler E-Mails (Registrierungsbestätigung, Passwort-Reset, Liga-Benachrichtigungen) nutzen wir
                den Dienst <strong>Mailjet</strong> (Sinch Sweden AB, Lindhagensgatan 74, 112 18 Stockholm, Schweden — EU-ansässig).
                Mailjet ist als Auftragsverarbeiter gemäß Art. 28 DSGVO tätig. Zwischen uns und Mailjet besteht ein
                Auftragsverarbeitungsvertrag (AVV). Deine E-Mail-Adresse wird ausschließlich für den Versand der oben genannten
                E-Mails an Mailjet übermittelt und nicht für Werbezwecke genutzt.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Cookies und lokaler Speicher</h2>
              <p>
                Diese Website verwendet keine Tracking- oder Werbe-Cookies. Wir setzen ausschließlich technisch notwendige
                Mechanismen ein:
              </p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li><strong>JWT-Token (localStorage)</strong> — zur Aufrechterhaltung deiner Anmeldesitzung</li>
                <li><strong>Spracheinstellung (localStorage)</strong> — zur Speicherung deiner gewählten Sprache</li>
                <li><strong>Cookie-Einwilligung (localStorage)</strong> — zur Speicherung deiner Bestätigung dieses Banners</li>
              </ul>
              <p className="mt-2">
                Diese Daten verlassen deinen Browser nicht und werden nicht an Dritte übermittelt.
                Eine Einwilligung nach § 25 TDDDG ist für technisch notwendige Speicherung nicht erforderlich.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Hosting</h2>
              <p>
                Die Plattform wird auf einem Server in Deutschland betrieben. Beim Aufruf der Website werden
                technische Verbindungsdaten (IP-Adresse, Browsertyp, Uhrzeit) im Rahmen von Server-Logs vorübergehend gespeichert.
                Diese werden nach spätestens 7 Tagen automatisch gelöscht und nicht mit anderen Daten zusammengeführt.
                Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an Sicherheit und Betrieb).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Deine Rechte</h2>
              <p className="mb-2">Du hast nach der DSGVO folgende Rechte:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Auskunft</strong> (Art. 15) — Welche Daten wir über dich gespeichert haben</li>
                <li><strong>Berichtigung</strong> (Art. 16) — Korrektur unrichtiger Daten (direkt in den Profileinstellungen möglich)</li>
                <li><strong>Löschung</strong> (Art. 17) — Löschung deines Kontos und aller personenbezogenen Daten</li>
                <li><strong>Einschränkung</strong> (Art. 18) — Einschränkung der Verarbeitung</li>
                <li><strong>Datenportabilität</strong> (Art. 20) — Erhalt deiner Daten in maschinenlesbarem Format</li>
                <li><strong>Widerspruch</strong> (Art. 21) — Widerspruch gegen Verarbeitung auf Basis berechtigter Interessen</li>
                <li><strong>Widerruf</strong> — Widerruf von Einwilligungen jederzeit mit Wirkung für die Zukunft</li>
              </ul>
              <p className="mt-3">
                Zur Ausübung deiner Rechte kontaktiere uns unter:{' '}
                <a href="mailto:grenzland-liga@padelmap.app" className="text-primary-600 hover:text-primary-700">grenzland-liga@padelmap.app</a>
              </p>
              <p className="mt-2">
                Du hast außerdem das Recht, dich bei einer Datenschutz-Aufsichtsbehörde zu beschweren.
                Zuständig ist das <strong>Unabhängige Landeszentrum für Datenschutz Schleswig-Holstein (ULD)</strong>,{' '}
                <a href="https://www.datenschutzzentrum.de" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">
                  www.datenschutzzentrum.de
                </a>.
              </p>
            </section>
          </>
        )}

      </div>
    </div>
  );
}
